import type { AssignedPriceMapper } from '@domains/subscription/priv/Billables/api/mappers'
import { LinearAssignedPriceMapper } from '@domains/subscription/priv/Billables/usecases/Price/linear/linearMapper'
import { MaxAssignedPriceMapper } from '@domains/subscription/priv/Billables/usecases/Price/max/maxMapper'
import { MinAssignedPriceMapper } from '@domains/subscription/priv/Billables/usecases/Price/min/minMapper'
import { TieredAssignedPriceMapper } from '@domains/subscription/priv/Billables/usecases/Price/tiered/tieredMapper'
import type { BillableItem, TemplateAssignedPrice } from '@mynt/backoffice-api-spec/billables'
import {
  BillingInterval,
  type Channel,
  type ConnectedTemplate,
  type CountrySupport as CountrySupportDto,
  type CreateDefaultConfig,
  type DefaultConfig as DefaultConfigDto,
  type Eligibility as EligibilityDto,
  type PartnerChannelChannelIdEnum,
  type PartnerSupport as PartnerSupportDto,
  type PartnerSupportPartnersEnum,
  SupportType as SupportTypeDto,
  type Template,
  type TemplateCreate,
} from '@mynt/backoffice-api-spec/templates'
import {
  type Billable,
  type BillablePrice,
  BillingFrequency,
  ChannelType,
  type Country,
  type CountrySupport,
  type CreateDefaultConfigFormData,
  type Currency,
  type DefaultConfig,
  type DefaultConfigChannel,
  type Eligibility,
  type FeatureData,
  type Partner,
  type PartnerSupport,
  type PricePlanTemplate,
  SupportType,
  type UnsupportedBillablePrice,
} from '../types'

export const mapTemplateDtoToTemplate = (dtoTemplate: Template): PricePlanTemplate => {
  return {
    id: dtoTemplate.id || '',
    name: dtoTemplate.name || '',
    externalName: dtoTemplate.externalName || '',
    features: [] as FeatureData[],
    billables: [] as BillablePrice[],
    numberOfConnections: 0,
    eligibility: mapEligibilityDtoToEligibility(dtoTemplate.eligibility),
  }
}

export const mapEligibilityDtoToEligibility = (eligibilityDto: EligibilityDto): Eligibility => {
  return {
    partners: mapPartnerEligibilityToPartnerSupport(eligibilityDto.partners),
    currencies: mapEligibilityCurrencies(eligibilityDto.currencies),
    countries: mapCountryEligibilityToCountrySupport(eligibilityDto.countries),
    billingFrequency: mapBillingIntervalToBillingFrequency(eligibilityDto.billingInterval),
  }
}

const mapPartnerEligibilityToPartnerSupport = (eligibilityPartner: PartnerSupportDto | undefined): PartnerSupport | undefined => {
  if (!eligibilityPartner) return undefined

  if (eligibilityPartner.type === SupportTypeDto.ALL) {
    return {
      type: SupportType.ALL,
      values: undefined,
    }
  }

  return {
    type: SupportType.SPECIFIC,
    values: eligibilityPartner.partners as Partner[],
  }
}

const mapCountryEligibilityToCountrySupport = (eligibilityCountry: CountrySupportDto | undefined): CountrySupport | undefined => {
  if (!eligibilityCountry) return undefined

  if (eligibilityCountry.type === SupportTypeDto.ALL) {
    return {
      type: SupportType.ALL,
      values: undefined,
    }
  }

  return {
    type: SupportType.SPECIFIC,
    values: eligibilityCountry.countries as Country[],
  }
}

export const mapBillingIntervalToBillingFrequency = (billingInterval: BillingInterval): BillingFrequency => {
  switch (billingInterval) {
    case BillingInterval.MONTHLY:
      return BillingFrequency.MONTHLY
    case BillingInterval.YEARLY:
      return BillingFrequency.YEARLY
  }
}

const mapEligibilityCurrencies = (eligibilityCurrencies: EligibilityDto['currencies']): PricePlanTemplate['eligibility']['currencies'] => {
  if (eligibilityCurrencies?.type === SupportTypeDto.ALL) {
    return {
      type: SupportType.ALL,
      values: undefined,
    }
  }

  return {
    type: SupportType.SPECIFIC,
    values: eligibilityCurrencies?.currencies as Currency[],
  }
}

export const mapConnectedTemplateDtoToTemplate = (dtoTemplate: ConnectedTemplate): PricePlanTemplate => {
  return {
    ...mapTemplateDtoToTemplate(dtoTemplate),
    numberOfConnections: dtoTemplate.numberOfConnections,
  }
}

export const mapEligibilityToEligibilityDto = (eligibility: Eligibility): EligibilityDto => {
  const partialEligibilityDto: Partial<EligibilityDto> = {}

  if (eligibility.partners) {
    partialEligibilityDto.partners = {
      type: eligibility.partners.type as SupportTypeDto,
      partners: eligibility.partners.values as PartnerSupportPartnersEnum[] | undefined,
    }
  }

  if (eligibility.currencies) {
    partialEligibilityDto.currencies = {
      type: eligibility.currencies.type as SupportTypeDto,
      currencies: eligibility.currencies.values,
    }
  }

  if (eligibility.countries) {
    partialEligibilityDto.countries = {
      type: eligibility.countries.type as SupportTypeDto,
      countries: eligibility.countries.values,
    }
  }

  return {
    ...partialEligibilityDto,
    billingInterval: mapBillingFrequencyToBillingIntervals(eligibility.billingFrequency),
  }
}

const mapBillingFrequencyToBillingIntervals = (billingFrequency: BillingFrequency): BillingInterval => {
  switch (billingFrequency) {
    case BillingFrequency.MONTHLY:
      return BillingInterval.MONTHLY
    case BillingFrequency.YEARLY:
      return BillingInterval.YEARLY
  }
}

export const mapToTemplateCreateDto = (name: string, externalName: string, eligibility: Eligibility): TemplateCreate => {
  return {
    name: name,
    externalName: externalName,
    eligibility: mapEligibilityToEligibilityDto(eligibility),
  }
}

export const mapBillableItem = (dtoBillable: BillableItem): Billable => ({
  id: dtoBillable.id,
  name: dtoBillable.name,
  unit: dtoBillable.unitName,
})

export const mapAssignedPriceToPrice = (dtoAssignedPrice: TemplateAssignedPrice): BillablePrice | UnsupportedBillablePrice => {
  const mappers: AssignedPriceMapper[] = [
    new LinearAssignedPriceMapper(),
    new TieredAssignedPriceMapper(),
    new MaxAssignedPriceMapper(),
    new MinAssignedPriceMapper(),
  ]

  for (const mapper of mappers) {
    if (mapper.supports(dtoAssignedPrice)) {
      return mapper.toBillablePrice(dtoAssignedPrice)
    }
  }

  return { supported: false, billableId: dtoAssignedPrice.billableId }
}

export const mapDefaultConfigDtoToDefaultConfig = (dto: DefaultConfigDto): DefaultConfig => ({
  id: dto.id,
  channel: {
    type: dto.channel.type as ChannelType,
    channelId: dto.channel.channelId,
  },
  templateId: dto.templateId,
  currencySupport: {
    type: dto.currencySupport.type as SupportType,
    currencies: dto.currencySupport.currencies,
  },
})

const mapDefaultConfigChannelToChannelDto = (channel: DefaultConfigChannel): Channel => {
  switch (channel.type) {
    case ChannelType.AGENCY:
      return {
        type: 'AGENCY',
        channelId: channel.channelId,
      }

    case ChannelType.PARTNER:
      return {
        type: 'PARTNER',
        channelId: channel.channelId as PartnerChannelChannelIdEnum,
      }
  }
}

export const mapCreateDefaultConfigFormDataToCreateDefaultConfigDto = (formData: CreateDefaultConfigFormData): CreateDefaultConfig => ({
  channel: mapDefaultConfigChannelToChannelDto(formData.channel),
  currencySupport: formData.currencySupport,
  templateId: formData.templateId,
})
